/**
 * Authentication Context
 */
import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refresh_token'));
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token && user) {
      // Token and user both exist in localStorage, validate them
      validateToken();
    } else if (token) {
      // Token exists but no user, fetch user data
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const validateToken = async () => {
    try {
      // Validate token by fetching current user
      const response = await api.get('/auth/me');
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Token validation failed:', error);
      // Token is invalid, try to refresh if we have a refresh token
      if (refreshToken) {
        await tryRefreshToken();
      } else {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const tryRefreshToken = async () => {
    try {
      const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
      const { token: newToken } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      // Try to fetch user again with new token
      await fetchCurrentUser();
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, refresh_token, user } = response.data;

    localStorage.setItem('token', token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(user));

    setToken(token);
    setRefreshToken(refresh_token);
    setUser(user);
    setIsAuthenticated(true);

    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{user, token, refreshToken, isAuthenticated, loading, login, logout}}>
      {children}
    </AuthContext.Provider>
  );
}
